import React from 'react';

/**
 * `true` when the media query matches. Always `false` on the server AND on the
 * first client render, then corrected in an effect — that ordering is the whole
 * point. Reading `window.matchMedia` during render would make the markup React
 * hydrates differ from the markup the server sent, which is a hydration error
 * on every narrow-viewport page load.
 *
 * The consequence is real and has to be designed around: for one frame after
 * hydration, a narrow viewport still reports "wide". So this hook is for
 * BEHAVIOUR (which element owns focus, whether Escape closes something), never
 * for deciding what is on screen. What is on screen is CSS's job, where the
 * media query is evaluated before the first paint.
 */
export default function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The width at which Semantic's `stackable` Grid drops the search block's facet
 * rail under the results (measured: two columns at 768, one at 767). The facet
 * drawer takes over at exactly that width, so keep this in lockstep with
 * `@pp-bp-stackable` in theme/tokens.less — if the two ever disagree there is a
 * band of widths with a full-height rail above the results and no way to close
 * it.
 */
export const STACKABLE = '(max-width: 767px)';
